import React, { useState } from 'react';
import { Landmark, Loader2, CheckCircle2, XCircle, FileText, Clock, AlertTriangle, ShoppingBag, Layers, FileDown } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { receiptUrl } from './StanbicCipsPanel';
import { downloadPdf } from '../lib/download';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentReviewPanel({ order, token, onUpdated }) {
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [autoProcure, setAutoProcure] = useState(true);
  const payment = order.payment;
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const is1688 = /1688\.com\/offer\/\d+/.test(order.product_url || '');
  const isImage = payment?.receipt?.content_type?.startsWith('image/');

  const act = async (kind) => {
    setBusy(kind);
    const waWin = window.open('', '_blank');
    try {
      const body = kind === 'confirm' ? { note, auto_procure: autoProcure && is1688 } : { note };
      const res = await fetch(`${API}/api/payments/admin/${order.id}/${kind}`, { method: 'POST', headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Action failed');
      if (kind === 'confirm') {
        toast.success(`Payment verified — order #${order.id} moved to Domestic Transit`);
        if (data.procurement) toast.success(`1688 order ${data.procurement.alibaba_order_id} created (¥${data.procurement.total_rmb})`);
        else if (data.procurement_error) toast.warning(`1688 auto-order skipped: ${data.procurement_error}`);
      } else toast.success('Receipt rejected — client notified');
      setNote(''); setRejecting(false);
      onUpdated();
      if (waWin) waWin.location.href = data.whatsapp_url;
    } catch (err) { if (waWin) waWin.close(); toast.error(err.message); }
    finally { setBusy(''); }
  };

  if (order.status !== 'pending_payment') {
    return payment?.status === 'verified' ? (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 flex items-center gap-2" data-testid={`payment-verified-${order.id}`}>
        <CheckCircle2 className="w-4 h-4" /> {payment.method === 'stanbic_cips' ? 'Stanbic CIPS' : 'Payment'} verified {new Date(payment.verified_at).toLocaleString()} by {payment.verified_by} · Ref {payment.reference}{payment.batch_id ? ' (combined)' : ''}
        {payment.receipt && <a href={receiptUrl(payment.receipt)} target="_blank" rel="noopener noreferrer" className="ml-auto underline font-medium">Receipt</a>}
      </div>
    ) : null;
  }

  const awaiting = payment?.status === 'awaiting_verification';
  const inBatch = payment?.batch_id && ['batched', 'awaiting_verification'].includes(payment.status);
  const proformaBtn = (
    <button type="button" onClick={() => downloadPdf(`${API}/api/payments/admin/${order.id}/proforma`, { Authorization: `Bearer ${token}` }, `Proforma-${payment?.reference || order.id}.pdf`)}
      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline font-medium" data-testid={`payment-proforma-${order.id}`}><FileDown className="w-3.5 h-3.5" /> Proforma PDF</button>
  );
  if (inBatch) {
    return (
      <div className="rounded-xl border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900 flex flex-wrap items-center gap-2" data-testid={`payment-in-batch-${order.id}`}>
        <Layers className="w-4 h-4 text-violet-700" /> Part of combined payment <span className="font-mono font-semibold">{payment.reference}</span>
        {awaiting ? <span className="text-amber-700 font-semibold">— receipt uploaded, verify it from the combined payment card at the top</span> : <span>— client has not uploaded the combined receipt yet</span>}
        <span className="ml-auto">{proformaBtn}</span>
      </div>
    );
  }
  return (
    <div className={`rounded-xl border p-4 ${awaiting ? 'border-amber-300 bg-amber-50' : 'border-blue-200 bg-blue-50'}`} data-testid={`payment-review-${order.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="font-bold text-sm text-gray-900 flex items-center gap-2"><Landmark className="w-4 h-4 text-blue-700" /> Stanbic CIPS Payment</span>
        {awaiting && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded border bg-amber-100 text-amber-800 border-amber-300 flex items-center gap-1" data-testid={`payment-awaiting-badge-${order.id}`}><Clock className="w-3 h-3" /> Awaiting verification</span>}
        {payment?.status === 'rejected' && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded border bg-red-100 text-red-800 border-red-300">Receipt rejected</span>}
        {!payment && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded border bg-gray-100 text-gray-600 border-gray-300">No receipt yet</span>}
        <span className="ml-auto">{proformaBtn}</span>
      </div>

      {payment?.receipt && (
        <div className="mt-3 flex flex-col sm:flex-row gap-3 text-xs text-gray-700">
          <a href={receiptUrl(payment.receipt)} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" data-testid={`payment-receipt-link-${order.id}`}>
            {isImage ? <img src={receiptUrl(payment.receipt)} alt="Receipt" className="w-32 h-32 object-cover rounded-lg border bg-white" /> : (
              <div className="w-32 h-32 rounded-lg border bg-white flex flex-col items-center justify-center text-blue-700 gap-1"><FileText className="w-8 h-8" /><span className="font-semibold">Open PDF</span></div>
            )}
          </a>
          <div className="space-y-1">
            <p><span className="text-gray-400">Reference:</span> <span className="font-mono font-semibold">{payment.reference}</span></p>
            <p><span className="text-gray-400">Expected:</span> <span className="font-semibold">¥{payment.amount_rmb?.toFixed(2)} CNY</span></p>
            <p><span className="text-gray-400">File:</span> {payment.receipt.filename}</p>
            <p><span className="text-gray-400">Uploaded:</span> {new Date(payment.receipt.uploaded_at).toLocaleString()}</p>
            {payment.reject_note && <p className="text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Rejected: {payment.reject_note}</p>}
          </div>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-gray-200/70 space-y-2">
        {is1688 && !order.procurement && (
          <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
            <input type="checkbox" checked={autoProcure} onChange={(e) => setAutoProcure(e.target.checked)} className="accent-orange-600" data-testid={`auto-procure-toggle-${order.id}`} />
            <ShoppingBag className="w-3.5 h-3.5 text-orange-600" /> Auto-place the 1688 purchase order on approval (default receiving address)
          </label>
        )}
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={rejecting ? 'Reason for rejection (sent to client)…' : 'Verification note (optional)…'}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:border-orange-500 focus:outline-none" data-testid={`payment-note-${order.id}`} />
        <div className="flex flex-col sm:flex-row gap-2">
          {!rejecting ? (
            <>
              <Button onClick={() => act('confirm')} disabled={!!busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold" data-testid={`approve-payment-btn-${order.id}`}>
                {busy === 'confirm' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Approve CIPS RMB → Domestic Transit
              </Button>
              {awaiting && <Button variant="outline" onClick={() => setRejecting(true)} disabled={!!busy} className="border-red-300 text-red-700 hover:bg-red-50 text-xs" data-testid={`reject-receipt-btn-${order.id}`}><XCircle className="w-4 h-4 mr-1" /> Reject receipt</Button>}
            </>
          ) : (
            <>
              <Button onClick={() => act('reject')} disabled={!!busy || !note.trim()} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold" data-testid={`reject-receipt-submit-${order.id}`}>
                {busy === 'reject' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />} Send rejection to client
              </Button>
              <Button variant="outline" onClick={() => { setRejecting(false); setNote(''); }} className="text-xs" data-testid={`reject-receipt-cancel-${order.id}`}>Back</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
